import { type File, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ImagePreviewModal } from '@/components/modals/ImagePreviewModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { XStack } from '@/components/ui';
import { Skeleton } from '@/components/ui/skeleton';
import { useHydratedFiles } from '@/hooks/useHydratedFile';
import { uiStore } from '@/stores/ui.store';
import { selectAdaptiveImageVariants } from '@/utils/files';

const THUMBNAIL_SIZE_CLASS = 'size-16';

export const ReviewImages = observer(({ fileIds }: { fileIds: string[] }) => {
  const hydrated = useHydratedFiles(fileIds);
  const available = selectAdaptiveImageVariants(
    hydrated
      .filter((entry) => entry.status !== 'unavailable')
      .map((entry, index) => ({
        ...entry,
        filename: entry.file?.filename ?? `unresolved:${index}`,
      })),
    uiStore.theme
  );
  if (available.length === 0) return null;

  return (
    <XStack className="flex-wrap items-center gap-2">
      <span className="self-center text-xs leading-none text-foreground-muted">
        Checked
      </span>
      {available.map((entry, index) =>
        entry.file ? (
          <ReviewImageThumbnail key={entry.file.id} file={entry.file} />
        ) : (
          <Skeleton
            key={`review-loading-${index}`}
            variant="low-emphasis"
            className={`${THUMBNAIL_SIZE_CLASS} rounded-lg`}
          />
        )
      )}
    </XStack>
  );
});

const ReviewImageThumbnail = observer(({ file }: { file: File }) => {
  const thumbnail = useThumbnailUrl(file);

  switch (thumbnail.status) {
    case 'loading':
      return (
        <Skeleton
          variant="low-emphasis"
          className={`${THUMBNAIL_SIZE_CLASS} rounded-lg`}
        />
      );
    case 'failed':
      return (
        <div
          className={`${THUMBNAIL_SIZE_CLASS} flex items-center justify-center rounded-lg bg-surface-active`}
        >
          <Icons.brokenImage className="size-4 text-foreground-muted" />
        </div>
      );
    case 'ready':
      return (
        <button
          type="button"
          aria-label={`View ${file.filename} full size`}
          onClick={() =>
            ModalsManager.show(ImagePreviewModal, {
              url: thumbnail.url,
              filename: file.filename,
            })
          }
          className={`${THUMBNAIL_SIZE_CLASS} shrink-0 overflow-hidden rounded-lg border border-border-strong smoothing-default transition-opacity hover:opacity-90`}
        >
          <img
            src={thumbnail.url}
            alt={file.filename}
            className="size-full object-cover"
            draggable={false}
          />
        </button>
      );
    default: {
      const _exhaustive: never = thumbnail;
      return _exhaustive;
    }
  }
});

type ThumbnailUrl =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'failed' };

const useThumbnailUrl = (file: File): ThumbnailUrl => {
  const { files: filesStore } = useStores();
  const [thumbnail, setThumbnail] = useState<ThumbnailUrl>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    filesStore.getFileUrl(file).then(
      (url) => !cancelled && setThumbnail({ status: 'ready', url }),
      () => !cancelled && setThumbnail({ status: 'failed' })
    );
    return () => {
      cancelled = true;
    };
  }, [filesStore, file]);

  return thumbnail;
};
